// src/components/SuperAdmin/LegalPolicyGovernance.jsx
// Z13 — SuperAdmin governance surface for Legal / Jurisdiction Policies (db.legal_policies).
//
// THE TENANT'S SECOND GOVERNED DIMENSION. The Industry Profile says WHICH retention classes apply to
// a business; this says WHAT value applies for its jurisdiction. They are independent, which is why
// this is its own surface and not a tab inside Industry Profiles — folding them together would mean
// a separate profile per jurisdiction.
//
// THIS SURFACE AUTHORS POLICY, NOT COMMERCIAL STATE. It never writes release state, entitlement or
// the feature catalogue: whether the capability may be provisioned is Commercial Control's answer,
// and this page does not touch it.
//
// IT RENDERS THE BACKEND'S ANSWER, NEVER ITS OWN. Publishability is computed server-side and shown
// verbatim; the Publish button is disabled from that response rather than from a rule re-implemented
// here, so the UI can never disagree with the authority about what is publishable.
//
// NO LEGAL CLAIM IS MADE ANYWHERE ON THIS PAGE. A published policy is a record of a human decision.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = '/api/v1/superadmin/legal-policies';

const LIFECYCLE_TONE = { active: 'default', draft: 'secondary', suspended: 'outline' };
const REVIEW_TONE = { approved: 'default', in_review: 'secondary', rejected: 'destructive' };

const arr = (v) => (Array.isArray(v) ? v : []);

// Human text for the machine reasons the publish gate returns. The backend stays authoritative —
// this only makes its answer readable, and an unmapped reason is shown verbatim rather than hidden.
const PROBLEM_TEXT = {
  review_not_approved: 'Legal/privacy review has not been approved for this version.',
  review_metadata_incomplete: 'The review is missing a reviewer, role or organisation.',
  values_without_source: 'One or more retention values cite no source authority.',
  source_refs_unresolved: 'A retention value cites a source that this version does not declare.',
};

const LegalPolicyGovernance = () => {
  const { superAdmin } = useSuperAdminAuth();
  const { toast } = useToast();
  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [gate, setGate] = useState({});            // policy_id -> server publishability answer
  const [draft, setDraft] = useState({});          // policy_id -> pending edits
  const [review, setReview] = useState({});        // policy_id -> pending review entry
  const [creating, setCreating] = useState(false);
  const [newPolicy, setNewPolicy] = useState({ policy_key: '', display_name: '', country: '', region: '' });

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(BASE);
      setPolicies(arr(r.data));
    } catch (e) {
      toast({ title: 'Could not load legal policies', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  // Ask the SERVER what still blocks publication. Never computed here.
  const refreshGate = useCallback(async (id) => {
    try {
      const r = await api.get(`${BASE}/${id}/publishability`);
      setGate((g) => ({ ...g, [id]: r.data }));
    } catch { /* a missing gate simply leaves Publish disabled */ }
  }, [api]);

  const openDetail = async (p) => {
    const next = openId === p.id ? null : p.id;
    setOpenId(next);
    if (next && p.lifecycle === 'draft') await refreshGate(p.id);
  };

  const act = async (id, fn, okTitle) => {
    setBusyId(id);
    try {
      await fn();
      toast({ title: okTitle });
      await fetchPolicies();
      await refreshGate(id);
    } catch (e) {
      const d = e?.response?.data;
      // Surface the governed refusal in full — a publish denial names exactly what is missing, and
      // hiding that behind "failed" would make the gate feel arbitrary.
      const detail = typeof d?.detail === 'string' ? d.detail : JSON.stringify(d?.detail || d || e.message);
      toast({ title: 'Refused by the governance authority', description: detail, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const createPolicy = () => act('new', async () => {
    await api.post(BASE, { ...newPolicy, legal_regime_ids: [], sources: [], retention_values: {} });
    setCreating(false);
    setNewPolicy({ policy_key: '', display_name: '', country: '', region: '' });
  }, 'Draft policy created');

  const saveDraft = (p) => act(p.id, async () => {
    const d = draft[p.id] || {};
    const patch = {};
    if (d.display_name !== undefined) patch.display_name = d.display_name;
    if (d.country !== undefined) patch.country = d.country;
    if (d.region !== undefined) patch.region = d.region;
    if (d.subregion !== undefined) patch.subregion = d.subregion;
    if (d.effective_from !== undefined) patch.effective_from = d.effective_from;
    if (d.sourcesText !== undefined) patch.sources = parseSources(d.sourcesText);
    if (d.valuesText !== undefined) patch.retention_values = JSON.parse(d.valuesText || '{}');
    await api.put(`${BASE}/${p.id}`, patch);
  }, 'Draft saved');

  const submitReview = (p) => act(p.id, async () => {
    const r = review[p.id] || {};
    await api.post(`${BASE}/${p.id}/review`, {
      review_status: r.review_status || 'approved',
      reviewed_by: r.reviewed_by || '',
      reviewer_role: r.reviewer_role || '',
      reviewer_organization: r.reviewer_organization || '',
      review_notes: r.review_notes || '',
    });
  }, 'Review recorded');

  const setLifecycle = (p, lifecycle, label) =>
    act(p.id, () => api.post(`${BASE}/${p.id}/lifecycle`, { lifecycle }), label);

  const newVersion = (p) =>
    act(p.id, () => api.post(`${BASE}/by-key/${p.policy_key}/versions`, { from_version: p.version }),
      'New draft version created');

  // Sources are edited as one line per authority: id | title | authority | type | reference
  const parseSources = (text) => String(text || '').split('\n').map((l) => l.trim()).filter(Boolean)
    .map((line) => {
      const [source_id = '', title = '', authority_name = '', authority_type = '', reference = ''] =
        line.split('|').map((x) => x.trim());
      return { source_id, title, authority_name, authority_type, reference };
    });

  const sourcesToText = (p) => arr(p.sources)
    .map((s) => [s.source_id, s.title, s.authority_name, s.authority_type, s.reference].join(' | '))
    .join('\n');

  const d = (id) => draft[id] || {};
  const setD = (id, k, v) => setDraft((x) => ({ ...x, [id]: { ...(x[id] || {}), [k]: v } }));
  const setR = (id, k, v) => setReview((x) => ({ ...x, [id]: { ...(x[id] || {}), [k]: v } }));

  if (loading) return <div className="p-6 text-gray-600">Loading legal / jurisdiction policies…</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Legal / Jurisdiction Policies</CardTitle>
          <CardDescription>
            Governed jurisdictional retention values and their provenance. The Industry Profile
            decides which retention classes apply to a business; a policy here decides what value
            applies in a jurisdiction. The two are assigned to a tenant independently in Tenant&nbsp;360.
            Publishing records a human decision — it makes no legal claim.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!creating ? (
            <Button onClick={() => setCreating(true)}>New policy (draft)</Button>
          ) : (
            <div className="space-y-2 border rounded p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-2">
                {['policy_key', 'display_name', 'country', 'region'].map((f) => (
                  <label key={f} className="text-sm">
                    <span className="block text-gray-600">{f.replace('_', ' ')}</span>
                    <input
                      className="border rounded px-2 py-1 w-full"
                      value={newPolicy[f]}
                      onChange={(e) => setNewPolicy({ ...newPolicy, [f]: e.target.value })}
                    />
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button disabled={!newPolicy.policy_key || busyId === 'new'} onClick={createPolicy}>Create draft</Button>
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {policies.length === 0 && (
        <Card><CardContent className="py-6 text-gray-600">
          No legal / jurisdiction policies have been authored yet.
        </CardContent></Card>
      )}

      {policies.map((p) => {
        const isDraft = p.lifecycle === 'draft';
        const g = gate[p.id];
        const problems = arr(g?.problems);
        return (
          <Card key={p.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">
                    {p.display_name || p.policy_key}{' '}
                    <span className="text-gray-500 font-normal">v{p.version}</span>
                  </CardTitle>
                  <CardDescription>
                    {[p.country, p.region, p.subregion].filter(Boolean).join(' / ') || 'No jurisdiction recorded'}
                    {p.effective_from ? ` · effective from ${p.effective_from}` : ''}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={LIFECYCLE_TONE[p.lifecycle] || 'outline'}>{p.lifecycle}</Badge>
                  <Badge variant={REVIEW_TONE[p.review_status] || 'outline'}>
                    review: {p.review_status || 'not_reviewed'}
                  </Badge>
                  <Button variant="outline" onClick={() => openDetail(p)}>
                    {openId === p.id ? 'Hide' : 'Open'}
                  </Button>
                </div>
              </div>
            </CardHeader>

            {openId === p.id && (
              <CardContent className="space-y-5">
                {/* ── OVERVIEW / AUTHORING ───────────────────────────────────────────────────── */}
                <section>
                  <h4 className="font-semibold mb-2">Overview</h4>
                  {!isDraft && (
                    <p className="text-sm text-gray-600 mb-2">
                      This version is published and immutable. Create a new version to change it —
                      tenants pinned to this version keep exactly these values.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {['display_name', 'country', 'region', 'subregion', 'effective_from'].map((f) => (
                      <label key={f} className="text-sm">
                        <span className="block text-gray-600">{f.replace(/_/g, ' ')}</span>
                        <input
                          className="border rounded px-2 py-1 w-full disabled:bg-gray-100"
                          disabled={!isDraft}
                          value={d(p.id)[f] !== undefined ? d(p.id)[f] : (p[f] || '')}
                          onChange={(e) => setD(p.id, f, e.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                </section>

                {/* ── AUTHORITIES / SOURCES ──────────────────────────────────────────────────── */}
                <section>
                  <h4 className="font-semibold mb-1">Authorities / sources</h4>
                  <p className="text-xs text-gray-600 mb-1">
                    One per line: <code>id | title | authority | type | reference</code>. Record a
                    citation or identifier — never the text of the instrument.
                  </p>
                  <textarea
                    className="border rounded px-2 py-1 w-full font-mono text-xs disabled:bg-gray-100"
                    rows={4}
                    disabled={!isDraft}
                    value={d(p.id).sourcesText !== undefined ? d(p.id).sourcesText : sourcesToText(p)}
                    onChange={(e) => setD(p.id, 'sourcesText', e.target.value)}
                  />
                </section>

                {/* ── RETENTION RULES ────────────────────────────────────────────────────────── */}
                <section>
                  <h4 className="font-semibold mb-1">Retention rules</h4>
                  <p className="text-xs text-gray-600 mb-1">
                    Keyed by the retention class the Industry Profile declares. This surface does not
                    define classes — a class not declared by a tenant&apos;s profile simply does not
                    apply to them. Each value that asserts something must cite a source above.
                  </p>
                  <textarea
                    className="border rounded px-2 py-1 w-full font-mono text-xs disabled:bg-gray-100"
                    rows={7}
                    disabled={!isDraft}
                    value={d(p.id).valuesText !== undefined
                      ? d(p.id).valuesText
                      : JSON.stringify(p.retention_values || {}, null, 2)}
                    onChange={(e) => setD(p.id, 'valuesText', e.target.value)}
                  />
                </section>

                {/* ── REVIEW / PUBLICATION ───────────────────────────────────────────────────── */}
                <section>
                  <h4 className="font-semibold mb-1">Review / publication</h4>
                  {p.reviewed_by ? (
                    <p className="text-sm text-gray-700">
                      Reviewed by <strong>{p.reviewed_by}</strong> ({p.reviewer_role || 'role not recorded'},{' '}
                      {p.reviewer_organization || 'organisation not recorded'}) at {p.reviewed_at || '—'}
                      {p.published_by ? ` · published by ${p.published_by} at ${p.published_at}` : ''}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">No review recorded for this version.</p>
                  )}
                  {isDraft && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {['reviewed_by', 'reviewer_role', 'reviewer_organization', 'review_notes'].map((f) => (
                        <label key={f} className="text-sm">
                          <span className="block text-gray-600">{f.replace(/_/g, ' ')}</span>
                          <input
                            className="border rounded px-2 py-1 w-full"
                            value={(review[p.id] || {})[f] || ''}
                            onChange={(e) => setR(p.id, f, e.target.value)}
                          />
                        </label>
                      ))}
                      <label className="text-sm">
                        <span className="block text-gray-600">review status</span>
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={(review[p.id] || {}).review_status || 'approved'}
                          onChange={(e) => setR(p.id, 'review_status', e.target.value)}
                        >
                          {['approved', 'in_review', 'rejected', 'not_reviewed'].map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                </section>

                {/* ── WHAT BLOCKS PUBLICATION — straight from the server ─────────────────────── */}
                {isDraft && g && !g.publishable && (
                  <div className="border rounded p-3 bg-amber-50 text-sm">
                    <div className="font-semibold mb-1">Cannot publish yet</div>
                    <ul className="list-disc ml-5">
                      {problems.map((k) => <li key={k}>{PROBLEM_TEXT[k] || k}</li>)}
                      {arr(g.unsourced_classes).length > 0 && (
                        <li>Values with no source: {arr(g.unsourced_classes).join(', ')}</li>
                      )}
                      {arr(g.unresolved_source_refs).length > 0 && (
                        <li>Unresolved citations: {arr(g.unresolved_source_refs).join(', ')}</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {isDraft && (
                    <>
                      <Button disabled={busyId === p.id} onClick={() => saveDraft(p)}>Save draft</Button>
                      <Button variant="outline" disabled={busyId === p.id} onClick={() => submitReview(p)}>
                        Record review
                      </Button>
                      <Button
                        disabled={busyId === p.id || !g?.publishable}
                        onClick={() => setLifecycle(p, 'active', 'Policy published')}
                      >
                        Publish
                      </Button>
                    </>
                  )}
                  {p.lifecycle === 'active' && (
                    <>
                      <Button variant="outline" disabled={busyId === p.id} onClick={() => newVersion(p)}>
                        Create new version
                      </Button>
                      <Button variant="outline" disabled={busyId === p.id}
                        onClick={() => setLifecycle(p, 'suspended', 'Policy suspended')}>
                        Suspend
                      </Button>
                    </>
                  )}
                  {p.lifecycle === 'suspended' && (
                    <Button variant="outline" disabled={busyId === p.id} onClick={() => newVersion(p)}>
                      Create new version
                    </Button>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default LegalPolicyGovernance;
